import React, { useState } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Select,
  Popconfirm,
  Input,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  InboxOutlined,
  UndoOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import PermissionRender from "../../../components/PermissionRender";

const { Option } = Select;

const parkingTypeColors = {
  street: "blue",
  underground: "purple",
  "enclosed above-ground": "green",
};

const parkingTypeLabels = {
  street: "Parking voirie",
  underground: "Parking souterrain",
  "enclosed above-ground": "Parking aérien fermé",
};

export const ParkingTable = ({
  parkings,
  loading,
  editingParking,
  isEditing,
  onView,
  onEdit,
  onDelete,
  onSave,
  onCancel,
  onArchive,
  onUnarchive,
}) => {
  const [filterType, setFilterType] = useState(null);
  const [filterArchived, setFilterArchived] = useState(null);
  const [searchText, setSearchText] = useState("");

  // Filter parkings by type, archived status, and search text
  let filteredParkings = parkings;

  // Filter by search text (name, name_ar, name_fr)
  if (searchText) {
    const lowerSearchText = searchText.toLowerCase();
    filteredParkings = filteredParkings.filter((parking) => {
      const name = (parking.name || "").toLowerCase();
      const nameAr = (parking.name_ar || "").toLowerCase();
      const nameFr = (parking.name_fr || "").toLowerCase();
      return (
        name.includes(lowerSearchText) ||
        nameAr.includes(lowerSearchText) ||
        nameFr.includes(lowerSearchText)
      );
    });
  }

  if (filterType) {
    filteredParkings = filteredParkings.filter(
      (parking) => parking.parking_type === filterType
    );
  }

  if (filterArchived === true || filterArchived === false) {
    const filterArchivedValue = filterArchived ? 1 : 0;
    filteredParkings = filteredParkings.filter(
      (parking) => parking.archived === filterArchivedValue
    );
  }

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <div>{name}</div>
          {record.name_fr && (
            <div style={{ fontSize: 12, color: "#999" }}>{record.name_fr}</div>
          )}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "parking_type",
      key: "parking_type",
      width: 180,
      render: (type) => (
        <Tag color={parkingTypeColors[type]}>
          {parkingTypeLabels[type] || type}
        </Tag>
      ),
    },
    {
      title: "Rue (ID)",
      dataIndex: "external_id",
      key: "external_id",
      width: 100,
      render: (id) =>
        id ? (
          <div>
            {id}
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => window.open(`/streets/${id}/lines`, "_blank")}
              style={{ marginLeft: 8 }}
            />
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Carte",
      key: "has_map_data",
      width: 100,
      align: "center",
      render: (_, record) => {
        const hasCoordinates =
          record.coordinates && record.coordinates.length > 0;
        const hasMarker = !!record.marker;

        if (hasCoordinates && hasMarker) {
          return (
            <Tooltip title="Polygone et marqueur disponibles">
              <Tag color="success" icon={<EnvironmentOutlined />}>
                Complet
              </Tag>
            </Tooltip>
          );
        } else if (hasCoordinates || hasMarker) {
          return (
            <Tooltip
              title={
                hasCoordinates ? "Polygone uniquement" : "Marqueur uniquement"
              }
            >
              <Tag color="warning" icon={<EnvironmentOutlined />}>
                Partiel
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tooltip title="Aucune donnée cartographique">
            <Tag color="default">Sans carte</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Tarifs",
      dataIndex: "tariff_info",
      key: "tariff_info",
      render: (tariff) => {
        const entries = Object.entries(tariff || {});
        return (
          <Tooltip
            title={entries.map(([key, value]) => (
              <div key={key}>
                {key}: {value} MAD
              </div>
            ))}
          >
            <span style={{ cursor: "pointer" }}>
              {entries.length} tarif{entries.length > 1 ? "s" : ""}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
      render: (notes) => notes || "-",
    },
    {
      title: "Statut",
      dataIndex: "archived",
      key: "archived",
      width: 100,
      render: (archived) => (
        <Tag color={archived ? "default" : "success"}>
          {archived ? "Archivé" : "Actif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 280,
      render: (_, record) => {
        const isCurrentlyEditing = editingParking?.id === record.id;

        if (isCurrentlyEditing) {
          return (
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                size="small"
                onClick={onSave}
              >
                Enregistrer
              </Button>
              <Button
                danger
                icon={<CloseOutlined />}
                size="small"
                onClick={onCancel}
              >
                Annuler
              </Button>
            </Space>
          );
        }

        return (
          <Space>
            <Tooltip
              title={
                record.coordinates
                  ? "Voir sur la carte"
                  : "Aucune donnée cartographique"
              }
            >
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => onView(record)}
                disabled={
                  (!record.coordinates || record.coordinates.length === 0) &&
                  !record.marker
                }
              >
                Voir
              </Button>
            </Tooltip>
            <PermissionRender
              requiredPermission={["citizen_parkings", "update"]}
            >
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => onEdit(record)}
                disabled={isEditing}
              >
                Modifier
              </Button>
            </PermissionRender>
            <PermissionRender
              requiredPermission={["citizen_parkings", "archive"]}
            >
              {record.archived ? (
                <Popconfirm
                  title="Désarchiver ce parking?"
                  description="Ce parking sera de nouveau actif."
                  onConfirm={() => onUnarchive(record.id)}
                  okText="Oui"
                  cancelText="Non"
                >
                  <Button icon={<UndoOutlined />} size="small" type="default">
                    Désarchiver
                  </Button>
                </Popconfirm>
              ) : (
                <Popconfirm
                  title="Archiver ce parking?"
                  description="Ce parking sera marqué comme archivé."
                  onConfirm={() => onArchive(record.id)}
                  okText="Oui"
                  cancelText="Non"
                >
                  <Button icon={<InboxOutlined />} size="small" type="default">
                    Archiver
                  </Button>
                </Popconfirm>
              )}
            </PermissionRender>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Rechercher par nom..."
            prefix={<SearchOutlined />}
            style={{ width: 250 }}
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <span>Filtrer par type:</span>
          <Select
            placeholder="Tous les types"
            style={{ width: 200 }}
            allowClear
            value={filterType}
            onChange={(value) => setFilterType(value)}
          >
            <Option value="street">
              <Tag color={parkingTypeColors.street}>
                {parkingTypeLabels.street}
              </Tag>
            </Option>
            <Option value="underground">
              <Tag color={parkingTypeColors.underground}>
                {parkingTypeLabels.underground}
              </Tag>
            </Option>
            <Option value="enclosed above-ground">
              <Tag color={parkingTypeColors["enclosed above-ground"]}>
                {parkingTypeLabels["enclosed above-ground"]}
              </Tag>
            </Option>
          </Select>

          <span>Filtrer par statut:</span>
          <Select
            placeholder="Tous les statuts"
            style={{ width: 150 }}
            allowClear
            value={filterArchived}
            onChange={(value) => setFilterArchived(value)}
          >
            <Option value={false}>
              <Tag color="success">Actif</Tag>
            </Option>
            <Option value={true}>
              <Tag color="default">Archivé</Tag>
            </Option>
          </Select>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredParkings.sort((a, b) => a.id - b.id)}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showTotal: (total) => `Total: ${total} parkings`,
        }}
        rowClassName={(record) =>
          editingParking?.id === record.id ? "ant-table-row-selected" : ""
        }
      />
    </div>
  );
};
